import { useContext, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { ensureAudioReady } from '../../utils/audioAlert';
import { Dropdown } from 'react-bootstrap';
import {
	FaExpand, FaVolumeUp, FaVolumeMute,
	FaSort, FaThLarge, FaPlus
} from 'react-icons/fa';
import useCapabilities from '../../hooks/useCapabilities';
import { isDashboardPathname } from '../../utils/dashboards';

const SORT_OPTIONS = [
	{ key: 'manual',             label: 'Manual (drag to reorder)' },
	{ key: 'name-asc',           label: 'Name A → Z' },
	{ key: 'name-desc',          label: 'Name Z → A' },
	{ key: 'type',               label: 'Type (Toggle / Heartbeat / Manual Clear)' },
	{ key: 'recent-desc',        label: 'Most recent activity' },
	{ key: 'recent-asc',         label: 'Least recent activity' },
	{ key: 'status-change-desc', label: 'Last status change (newest)' },
	{ key: 'status-change-asc',  label: 'Last status change (oldest)' },
	{ key: 'status-asc',         label: 'Status (Severe → Clear)' },
	{ key: 'status-desc',        label: 'Status (Clear → Severe)' },
];

const RightToolbarComponent = () => {
	const location = useLocation();

	const {
		state,
		actions: {
			toggleFullscreen, toggleAudio,
			setViewMode, setTileSortOrder, setTileColumns, setTileMinHeight,
			triggerCreateAgent, triggerCreateFromTemplate
		}
	} = useContext(ThemeContext);


	const isDashboardPage = isDashboardPathname(location.pathname);

	const tileColumns   = state.tileColumns   ?? 5;
	const tileMinHeight = state.tileMinHeight ?? 0;
	const viewMode      = state.viewMode      || 'tiles';
	const tileSortOrder = state.tileSortOrder || 'manual';

	const { canManageAgents } = useCapabilities();

	return (
		<div className="right-toolbar shadow-sm">
			<div className="rt-divider" />

			{/* Audio */}
			<button
				className="rt-btn"
				onClick={() => { ensureAudioReady(); toggleAudio(); }}
				title={state.audioEnabled ? 'Mute alerts' : 'Enable audio alerts'}
			>
				{state.audioEnabled ? <FaVolumeUp size={14} /> : <FaVolumeMute size={14} />}
			</button>

			{/* Fullscreen */}
			<button
				className="rt-btn"
				onClick={() => toggleFullscreen(true)}
				title="Fullscreen"
			>
				<FaExpand size={14} />
			</button>

			{isDashboardPage && (
				<>
					<div className="rt-divider" />

					{/* View mode toggle — always first after the divider so its position never shifts */}
					<button
						className="rt-btn rt-btn--symbol active"
						onClick={() => setViewMode(viewMode === 'tiles' ? 'list' : 'tiles')}
						title={viewMode === 'tiles' ? 'Switch to list view' : 'Switch to tile view'}
						aria-label={viewMode === 'tiles' ? 'Switch to list view' : 'Switch to tile view'}
					>{viewMode === 'tiles' ? '☰' : '⊞'}</button>

					{/* Layout (tiles only) */}
					{viewMode === 'tiles' && (
						<Dropdown drop="start" autoClose="outside">
							<Dropdown.Toggle
								as="button"
								className="rt-btn"
								title="Tile layout — width and height"
								aria-label="Tile layout controls"
							>
								<FaThLarge size={14} />
							</Dropdown.Toggle>
							<Dropdown.Menu className="tile-layout-dropdown">
								<div className="tile-layout-control">
									<div className="tile-layout-label">
										<span>Width</span>
										<span className="tile-layout-value">
											{['12/row','10/row','8/row','6/row','4/row','3/row','2/row'][tileColumns - 1] ?? '?/row'}
										</span>
									</div>
									<input
										type="range"
										min={1} max={7} step={1}
										value={tileColumns}
										onChange={e => setTileColumns(Number(e.target.value))}
										aria-label="Tile width"
										className="tile-layout-slider"
									/>
								</div>
								<div className="tile-layout-control">
									<div className="tile-layout-label">
										<span>Height</span>
										<span className="tile-layout-value">
											{['Auto','Short','Medium','Tall'][Math.max(0, [0,120,200,280].indexOf(tileMinHeight))]}
										</span>
									</div>
									<input
										type="range"
										min={0} max={3} step={1}
										value={Math.max(0, [0,120,200,280].indexOf(tileMinHeight))}
										onChange={e => setTileMinHeight([0,120,200,280][Number(e.target.value)])}
										aria-label="Tile height"
										className="tile-layout-slider"
									/>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					)}

					{/* Sort */}
					<Dropdown drop="start">
						<Dropdown.Toggle
							as="button"
							className={`rt-btn${tileSortOrder !== 'manual' ? ' active' : ''}`}
							title="Sort agents"
							aria-label="Sort agents"
						>
							<FaSort size={14} />
						</Dropdown.Toggle>
						<Dropdown.Menu>
							{SORT_OPTIONS.map(opt => (
								<Dropdown.Item
									key={opt.key}
									active={tileSortOrder === opt.key}
									onClick={() => setTileSortOrder(opt.key)}
								>
									{opt.label}
								</Dropdown.Item>
							))}
						</Dropdown.Menu>
					</Dropdown>

					{/* Add agent: from scratch, or from a template */}
					{canManageAgents && (
						<Dropdown drop="start">
							<Dropdown.Toggle
								as="button"
								className="rt-btn"
								title="Add new agent"
								aria-label="Add new agent"
							>
								<FaPlus size={12} />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item onClick={triggerCreateAgent}>New agent</Dropdown.Item>
								<Dropdown.Item onClick={triggerCreateFromTemplate}>New from template</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					)}
				</>
			)}
		</div>
	);
};

const RightToolbar = memo(RightToolbarComponent);
export default RightToolbar;
